import React, { useState, useRef } from 'react';
import { Star } from 'lucide-react';
import axios from 'axios';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../ui/hover-card';
import UserAvatar from './UserAvatar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

// In-memory cache so hovering the same user multiple times only fetches once
const cardCache = new Map();
const CACHE_MS = 60 * 1000;

const fetchCard = async (userId) => {
  const cached = cardCache.get(userId);
  if (cached && Date.now() - cached.t < CACHE_MS) return cached.data;
  try {
    const res = await axios.get(`${API}/users/${encodeURIComponent(userId)}/card`);
    cardCache.set(userId, { t: Date.now(), data: res.data });
    return res.data;
  } catch {
    return null;
  }
};

/**
 * BookwormCard — small hover popover that shows a member's mini-profile when
 * you hover their avatar/name on a review or chat message.
 *
 * Props mirror UserAvatar so it can wrap any avatar inline:
 *   <BookwormCard userId={...} name={...} avatarId={...} avatarPhotoUrl={...}>
 *     <UserAvatar ... />
 *   </BookwormCard>
 */
const BookwormCard = ({
  userId,
  name,
  avatarId,
  avatarPhotoUrl,
  children,
  align = 'start',
  side = 'top',
}) => {
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(false);
  const requested = useRef(false);

  const handleOpenChange = (open) => {
    if (open && !requested.current && userId) {
      requested.current = true;
      setLoading(true);
      fetchCard(userId).then((data) => {
        setCard(data);
        setLoading(false);
      });
    }
  };

  // Fall back gracefully if no userId (e.g. Anonymous reviewer)
  if (!userId) return children;

  const stats = card?.stats || {};
  const liveAvatarId = card?.avatar_id || avatarId;
  const liveAvatarPhoto = card?.avatar_photo_url || avatarPhotoUrl;
  const displayName = card?.name || name || 'Reader';
  const isAdmin = card?.role === 'admin';
  const subtitle = isAdmin ? 'Bookworm Admin' : 'Bookworm member';

  return (
    <HoverCard openDelay={250} closeDelay={120} onOpenChange={handleOpenChange}>
      <HoverCardTrigger asChild>
        <span data-testid={`bookworm-card-trigger-${userId}`} className="inline-flex items-center cursor-pointer">
          {children}
        </span>
      </HoverCardTrigger>
      <HoverCardContent
        align={align}
        side={side}
        className="w-72 bg-zinc-950/95 border border-fuchsia-500/30 backdrop-blur-md text-white shadow-xl shadow-fuchsia-500/5"
        data-testid={`bookworm-card-content-${userId}`}
      >
        <div className="flex items-start gap-3">
          <UserAvatar
            name={displayName}
            avatarId={liveAvatarId}
            avatarPhotoUrl={liveAvatarPhoto}
            size="xl"
            ringColor={isAdmin ? 'ring-amber-400/60' : 'ring-fuchsia-400/40'}
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 flex-wrap">
              <h4 className="text-base font-semibold truncate">{displayName}</h4>
              {isAdmin && (
                <span
                  className="text-[9px] uppercase font-semibold tracking-wider px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30"
                  data-testid={`bookworm-card-admin-badge-${userId}`}
                >
                  Admin
                </span>
              )}
            </div>
            <p
              className="text-xs text-fuchsia-300/80 mt-0.5"
              data-testid={`bookworm-card-subtitle-${userId}`}
            >
              {subtitle}
            </p>
          </div>
        </div>

        {loading && (
          <div className="mt-4 text-xs text-gray-500 text-center" data-testid={`bookworm-card-loading-${userId}`}>
            Loading reader card…
          </div>
        )}

        {!loading && card && (
          <div className="mt-4" data-testid={`bookworm-card-stats-${userId}`}>
            <Stat
              icon={<Star className="h-3.5 w-3.5 text-yellow-400" />}
              label="Reviews"
              value={`${stats.review_count || 0}`}
            />
          </div>
        )}

        {!loading && !card && (
          <p className="mt-4 text-xs text-gray-500 text-center">Profile unavailable</p>
        )}
      </HoverCardContent>
    </HoverCard>
  );
};

const Stat = ({ icon, label, value, hint, valueClass = '' }) => (
  <div className="bg-white/5 rounded-md px-2.5 py-2 border border-white/5">
    <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-gray-400 font-medium">
      {icon}
      <span>{label}</span>
    </div>
    <p className={`text-sm font-semibold mt-0.5 ${valueClass}`}>{value}</p>
    {hint && <p className="text-[10px] text-gray-500 mt-0.5">{hint}</p>}
  </div>
);

export default BookwormCard;
